import React, { useEffect, useRef } from "react";
import type {
  MembershipPricingOption,
  Price,
  PricingOption,
  ProductCardPrices
} from "@shared/graphql.types";

import { Accordion } from "packages/ui-shared/components/Accordion/Accordion";
import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button";

import {
  membershipContentContainer,
  membershipContentFooter,
  priceExplainerStyles
} from "./Pricing.css";
import { PAGE_ELEMENT_CLICKED_EVENT_KEY } from "tracking/hooks/usePageElementEvents";
import { dispatch } from "@shared/utils.events";
import { markHydrationComplete } from "basket_and_checkout/shared/utils/markHydrationComplete.ref";
import { MarkdownRenderer, Typography } from "@shared/ui.primitives";
import { ChevronDownIcon, ChevronUpIcon } from "@shared/ui.icons";
import { colours } from "@shared/ui.design-system";
import { StrikethroughPrice } from "@shared/ui.components";

const PRICING_COMPONENT_RADIO_NAME = "pricing-toggle";

type AccordionWithSingleMembershipOptionProps = {
  selectedValue?: string;
  onAccordionChange: (value: string) => void;
  prices: Price[];
  productPrices?: ProductCardPrices;
  setShowMembershipOptions: (sku: string | null) => void;
  defaultMembershipSku?: string | null;
  context?: string;
  isStrikethroughVariant?: boolean;
};

export const AccordionWithSingleMembershipOption: React.FC<
  AccordionWithSingleMembershipOptionProps
> = ({
  selectedValue,
  onAccordionChange,
  prices,
  productPrices,
  setShowMembershipOptions,
  defaultMembershipSku,
  context,
  isStrikethroughVariant
}) => {
  const radioName =
    PRICING_COMPONENT_RADIO_NAME + (context ? `-${context}` : "");

  // Ref for the content of the second accordion tab
  const secondContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedValue === "2" && secondContentRef.current) {
      // Find the first focusable element inside the content
      const focusable = secondContentRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }
  }, [selectedValue]);

  return (
    <Accordion
      selectedValue={selectedValue}
      onChange={onAccordionChange}
      radioName={radioName}
    >
      {prices.map(
        ({
          price,
          id
        }: {
          price: MembershipPricingOption | PricingOption;
          id: string;
        }) => {
          const priceMembership =
            "membership" in price ? price.membership : null;
          const currentProductPriceType = priceMembership
            ? productPrices?.member
            : productPrices?.guest;
          const priceText = isStrikethroughVariant ? (
            <StrikethroughPrice
              price={currentProductPriceType?.regular?.formatted || ""}
              discountedPrice={currentProductPriceType?.promo?.formatted || ""}
              variant="pricingOptionCard"
            />
          ) : (
            price.priceText
          );
          return (
            <Accordion.Item value={id} key={id} radioName={radioName}>
              <Accordion.Item.Label
                value={id}
                header={price.title}
                description={price.subtitle ?? ""}
                rightContent={priceText}
                radioName={radioName}
                headerVariant="body-md-bold"
                icon={
                  priceMembership &&
                  (selectedValue === id ? (
                    <ChevronUpIcon
                      height={20}
                      width={20}
                      fill={colours.primary.medium}
                    />
                  ) : (
                    <ChevronDownIcon
                      height={20}
                      width={20}
                      fill={colours.primary.medium}
                    />
                  ))
                }
              />
              {/* we don't render content for the second tab unless open to avoid tabbing into it when its visually hidden */}
              {priceMembership && selectedValue === "2" && (
                <Accordion.Item.Content>
                  <div
                    className={membershipContentContainer}
                    ref={id === "2" ? secondContentRef : undefined}
                  >
                    <Typography as="span" variant="body-sm-bold">
                      {priceMembership.name}
                    </Typography>
                    <div className={priceExplainerStyles}>
                      <Typography as="span" variant="body-sm">
                        {priceMembership.priceExplainer}
                      </Typography>
                    </div>
                    <MarkdownRenderer content={priceMembership.perks} />

                    {priceMembership.cta && (
                      <DeprecatedUISharedButton
                        buttonRef={markHydrationComplete}
                        handleClick={() => {
                          dispatch(PAGE_ELEMENT_CLICKED_EVENT_KEY, {
                            target: "og_pdp_panel",
                            details: "view_link",
                            location: "og_pdp_panel",
                            locationDetails: defaultMembershipSku,
                            target2: defaultMembershipSku,
                            details2: priceMembership?.cta?.title ?? ""
                          });
                          setShowMembershipOptions(priceMembership.sku);
                        }}
                        variant="link"
                      >
                        {priceMembership.cta.title}
                      </DeprecatedUISharedButton>
                    )}
                  </div>
                  {selectedValue && priceMembership.terms?.termsContent && (
                    <MarkdownRenderer
                      className={membershipContentFooter}
                      as="footer"
                      content={priceMembership.terms.termsContent}
                    />
                  )}
                </Accordion.Item.Content>
              )}
            </Accordion.Item>
          );
        }
      )}
    </Accordion>
  );
};
